import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";
import { type SortingState, type Updater } from "@tanstack/react-table";
import { ClipboardPenLine, Plus, Search, Trash2, X } from "lucide-react";

import { deleteManager, listManagers, listManagersReset } from "../../actions";
import { SortMenu } from "@/components/SortMenu";
import { WFPButton } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "../../components/ui/input-group";
import { getCountryObjectFromCache } from "../../utils";
import { DEFAULT_SORTING, List, MANAGER_SORT_OPTIONS } from "./components/List";
import { ConnectedManagerModal } from "./components/Modal";
import { type Manager } from "./types";

interface StateProps {
  isFetching: boolean;
  errorMessage: string;
  managers: Manager[];
}

interface ListManagersState {
  isFetching: boolean;
  errorMessage: string;
  data: {
    results: Manager[];
    count?: number;
  };
}

interface RootState {
  listManagers: ListManagersState;
}

interface DispatchProps {
  listManagers: () => void;
  listManagersReset: () => void;
  deleteManager: (id: number | string) => Promise<boolean>;
}

type ManagersProps = StateProps & DispatchProps;

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

const adaptManagers = (managers: Manager[]): Manager[] =>
  managers.map(manager => ({
    ...manager,
    countriesString: manager?.countries?.map(country => country.code).join(", "),
  }));

const filterManagers = (managers: Manager[], searchTerm: string) => {
  const query = searchTerm.trim().toLocaleLowerCase();

  if (!query) {
    return managers;
  }

  return managers.filter(manager =>
    [manager.first_name, manager.last_name, manager.email].some(value =>
      String(value ?? "")
        .toLocaleLowerCase()
        .includes(query)
    )
  );
};

export const Managers = ({
  isFetching,
  errorMessage,
  managers,
  listManagers,
  listManagersReset,
  deleteManager,
}: ManagersProps) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [managerToDelete, setManagerToDelete] = useState<Manager | null>(null);
  const [managerToEdit, setManagerToEdit] = useState<Manager | null | undefined>(undefined);

  useEffect(() => {
    listManagers();
    return () => {
      listManagersReset();
    };
  }, [listManagers, listManagersReset]);

  const visibleManagers = useMemo(
    () => filterManagers(managers, searchTerm),
    [managers, searchTerm]
  );

  const selectedSort = sorting[0];
  const selectedSortColumn = selectedSort?.id ?? "";
  const selectedSortDirection = selectedSort?.desc ? "desc" : "asc";

  const onSortingChange = (updater: Updater<SortingState>) => {
    setSorting(currentSorting =>
      typeof updater === "function" ? updater(currentSorting) : updater
    );
  };

  const onSortColumnChange = (sortColumn: string) => {
    setSorting([{ id: sortColumn, desc: selectedSortDirection === "desc" }]);
  };

  const onSortDirectionChange = (sortDirection: string) => {
    setSorting([
      {
        id: selectedSortColumn || MANAGER_SORT_OPTIONS[0].id,
        desc: sortDirection === "desc",
      },
    ]);
  };

  const onClickConfirmDeleteManager = () => {
    if (managerToDelete) {
      deleteManager(managerToDelete.id).then(() => listManagers());
    }
    setManagerToDelete(null);
  };

  const onClickCancelDeleteManager = () => setManagerToDelete(null);

  const closeManagerModal = () => setManagerToEdit(undefined);

  const onManagerSaved = () => {
    closeManagerModal();
    listManagers();
  };

  return (
    <>
      <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-x-2">
            <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 text-blue-600 bg-blue-100">
              <ClipboardPenLine />
            </span>
            <h1 className="font-semibold text-2xl">
              {t("managers.pageTitleWithCountry", {
                country: getCountryObjectFromCache()?.name ?? "",
              })}
            </h1>
          </div>
          <WFPButton type="button" onClick={() => setManagerToEdit(null)}>
            <Plus className="size-4" aria-hidden="true" />
            {t("managers.newManager")}
          </WFPButton>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <InputGroup className="w-full max-w-xs sm:w-80">
            <InputGroupAddon>
              <Search className="size-4" aria-hidden="true" />
            </InputGroupAddon>
            <InputGroupInput
              id="manager-search-input"
              type="search"
              value={searchTerm}
              className="[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
              placeholder={t("managers.searchPlaceholder")}
              aria-label={t("managers.searchPlaceholder")}
              onChange={event => setSearchTerm(event.target.value)}
            />
            {searchTerm && (
              <InputGroupAddon align="inline-end">
                <InputGroupButton
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={t("managers.clearFilter")}
                  onClick={() => setSearchTerm("")}
                >
                  <X className="size-3.5" aria-hidden="true" />
                </InputGroupButton>
              </InputGroupAddon>
            )}
          </InputGroup>
          <SortMenu
            options={MANAGER_SORT_OPTIONS}
            sortColumn={selectedSortColumn}
            sortDirection={selectedSortDirection}
            onSortColumnChange={onSortColumnChange}
            onSortDirectionChange={onSortDirectionChange}
          />
        </div>
        <List
          isFetching={isFetching}
          errorMessage={errorMessage}
          managers={visibleManagers}
          onClickView={setManagerToEdit}
          onClickDelete={setManagerToDelete}
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      </main>
      <ConnectedManagerModal
        manager={managerToEdit ?? null}
        isOpen={managerToEdit !== undefined}
        onClose={closeManagerModal}
        onSaved={onManagerSaved}
      />
      <Dialog
        open={Boolean(managerToDelete)}
        onOpenChange={open => {
          if (!open) {
            onClickCancelDeleteManager();
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-danger-50 text-danger-600">
                <Trash2 className="size-5" />
              </div>
              <div className="space-y-2">
                <DialogTitle>{t("managers.deleteManagerModalTitle")}</DialogTitle>
                <DialogDescription className="whitespace-pre-line text-foreground">
                  {t("managers.deleteManagerModalText", {
                    first_name: managerToDelete?.first_name,
                    last_name: managerToDelete?.last_name,
                  })}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter>
            <WFPButton type="button" variant="outline" onClick={onClickCancelDeleteManager}>
              {t("common.cancel")}
            </WFPButton>
            <WFPButton type="button" variant="destructive" onClick={onClickConfirmDeleteManager}>
              {t("common.delete")}
            </WFPButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const mapStateToProps = (state: RootState): StateProps => {
  const selectedCountry = getCountryObjectFromCache();
  return {
    isFetching: state.listManagers.isFetching,
    errorMessage: state.listManagers.errorMessage,
    managers: adaptManagers(state.listManagers.data.results).filter(manager =>
      manager.countries.map(({ id }) => id).includes(selectedCountry.id)
    ),
  };
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    listManagers: () => dispatchAction(dispatch, listManagers({ page: 1, page_size: 999 })),
    listManagersReset: () => dispatchAction(dispatch, listManagersReset()),
    deleteManager: (id: number | string) =>
      dispatchAction(dispatch, deleteManager(id)) as unknown as Promise<boolean>,
  };
};

export const ConnectedManagers = connect(mapStateToProps, mapDispatchToProps)(Managers);
